<template>
  <v-dialog eager v-model="ui.dialog" width="800" persistent content-class="signature-dialog">
    <v-card class="signatureCard">
      <div class="signatureCard__header">
        <div>
          <div class="signatureCard__eyebrow">
            {{ options.type == 'croquis' ? $t('signatureSign.save') : $t('signatureSign.sign') }}
          </div>
          <h2 class="signatureCard__title">
            {{
              options.type == 'croquis'
                ? $t('signatureSign.save')
                : $t('signatureSign.sign')
            }}
          </h2>
          <p class="signatureCard__subtitle" v-if="permiteCargarFirmaDesdeArchivo">
            Elige cómo quieres añadir la firma: manualmente o mediante imagen.
          </p>
        </div>
      </div>

      <v-card-text class="signatureCard__content">
        <v-form ref="form" lazy-validation v-model="ui.valid" style="height: 100%">
          <template v-if="permiteCargarFirmaDesdeArchivo">
            <div class="signatureModeSelector mb-5">
              <div
                class="signatureModeCard"
                :class="{ 'signatureModeCard--active': ui.modoFirma === 'manual' }">
                <div class="signatureModeCard__icon">
                  <v-icon color="primary">mdi-draw</v-icon>
                </div>

                <div class="signatureModeCard__body">
                  <div class="signatureModeCard__title">
                    Firma manual
                  </div>
                  <div class="signatureModeCard__text">
                    Firma directamente sobre el recuadro.
                  </div>
                </div>

                <div class="signatureModeCard__action">
                  <v-btn
                    small
                    depressed
                    :color="ui.modoFirma === 'manual' ? 'primary' : 'grey lighten-3'"
                    :class="{ 'white--text': ui.modoFirma === 'manual' }"
                    @click="activarModoManual">
                    Firma manual
                  </v-btn>
                </div>
              </div>

              <div
                class="signatureModeCard"
                :class="{ 'signatureModeCard--active': ui.modoFirma === 'archivo' }">
                <div class="signatureModeCard__icon">
                  <v-icon color="primary">mdi-image-plus</v-icon>
                </div>

                <div class="signatureModeCard__body">
                  <div class="signatureModeCard__title">
                    {{ hayArchivoFirmaSeleccionado ? 'Imagen seleccionada' : 'Cargar desde archivo' }}
                  </div>
                  <div class="signatureModeCard__text">
                    Sube una imagen con la firma ya preparada.
                  </div>
                </div>

                <div class="signatureModeCard__action">
                  <input
                    ref="inputArchivoFirma"
                    type="file"
                    accept="image/*"
                    style="display: none"
                    @change="alSeleccionarArchivoFirma" />

                  <v-btn
                    small
                    depressed
                    :color="ui.modoFirma === 'archivo' ? 'primary' : 'grey lighten-3'"
                    :class="{ 'white--text': ui.modoFirma === 'archivo' }"
                    @click="abrirSelectorArchivoFirma">
                    {{ hayArchivoFirmaSeleccionado ? 'Cambiar' : 'Seleccionar' }}
                  </v-btn>
                </div>
              </div>
            </div>
          </template>

          <v-row
            class="ma-0"
            v-show="!ui.generated && (!hayArchivoFirmaSeleccionado || ui.modoFirma === 'manual')">
            <v-col cols="12" class="pa-0">
              <div class="signaturePanel">
                <div class="signaturePanel__header">
                  <div class="signaturePanel__title">
                    {{ options.type == 'croquis' ? 'Área de croquis' : $t('signatureSign.signatureArea') }}
                  </div>
                  <div class="signaturePanel__hint">
                    {{ options.type == 'croquis' ? 'Dibuja aquí' : $t('signatureSign.signInsideBox') }}
                  </div>
                </div>

                <div id="signaturePad-wrapper" class="signaturePanel__canvasWrapper">
                  <canvas
                    id="signaturePad"
                    ref="signaturePad"
                    class="signaturePad"
                    style="opacity: 0"></canvas>
                </div>
              </div>
            </v-col>
          </v-row>

          <v-row v-if="hayArchivoFirmaSeleccionado && ui.modoFirma === 'archivo'" class="ma-0">
            <v-col cols="12" class="pa-0">
              <div class="signatureUploadPreview">
                <div class="signatureUploadPreview__header">
                  <div>
                    <div class="signatureUploadPreview__title">
                      {{ $t('signatureSign.uploadedFile') }}
                    </div>
                    <div class="signatureUploadPreview__name">
                      {{ archivoFirmaCargado.nombre }}
                    </div>
                  </div>

                  <v-btn
                    small
                    text
                    color="error"
                    class="signatureUploadPreview__removeBtn"
                    @click="cancelarArchivoFirmaSeleccionado">
                    <v-icon left small>mdi-close</v-icon>
                    Quitar
                  </v-btn>
                </div>

                <div class="signatureUploadPreview__imageWrapper">
                  <img
                    v-if="archivoFirmaCargado.base64"
                    :src="archivoFirmaCargado.base64"
                    alt="signature-upload-preview"
                    class="signaturePreview" />
                </div>
              </div>
            </v-col>
          </v-row>

          <v-row class="mt-4">
            <v-col :cols="12" :md="12" class="pt-0">
              <v-btn
                class="signatureMainButton"
                color="success"
                block
                depressed
                large
                @click="submit">
                <v-icon left small>{{ hayArchivoFirmaSeleccionado && ui.modoFirma === 'archivo' ? 'mdi-content-save' : 'mdi-check-circle' }}</v-icon>
                {{ textoBotonPrincipal }}
              </v-btn>
            </v-col>

            <v-col cols="12" class="pt-0 pb-0" v-if="ui.showErrorEmpty">
              <div class="signatureError">
                <v-icon color="red" style="font-size: 18px; margin-right: 8px;">mdi-close-circle</v-icon>
                {{ $t('signatureSign.noSignatureFound') }}
              </div>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions v-if="!ui.loading" class="signatureCard__actions">
        <v-btn
          text
          small
          color="error"
          class="signatureActionBtn"
          @click="alCancelar">
          <v-icon left small>mdi-arrow-left</v-icon>
          {{ $t('signatureSign.cancel') }}
        </v-btn>

        <v-spacer></v-spacer>

        <v-btn
          v-if="!hayArchivoFirmaSeleccionado || ui.modoFirma === 'manual'"
          color="deep-purple accent-4"
          text
          small
          class="signatureActionBtn"
          @click="cleanSignaturePad">
          <v-icon left small>mdi-broom</v-icon>
          {{ options.type == 'croquis' ? $t('signatureSign.clearSignaturePad') : $t('signatureSign.clearAndResign') }}
        </v-btn>
      </v-card-actions>

      <v-card-actions v-else class="signatureCard__loading">
        <v-progress-linear
          color="deep-purple accent-4"
          indeterminate
          rounded
          height="6">
        </v-progress-linear>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * @project: certiapp-nuxt
 * @file:    components/documentsUploader.vue
 * @desc:    ...
 * -------------------------------------------
 * Created Date: 13th July 2021
 * Last Modified: Fri May 05 2023
 **/

import SignaturePad from 'signature_pad'
import MODULOS from '~/models/modulos'

export default {
  name: 'Signatures',

  data: () => ({
    user: {},

    archivoFirmaCargado: {
      archivo: null,
      base64: null,
      nombre: '',
    },

    signaturePad: null,
    signatures: {
      primary: {},
      secondary: {},
    },

    options: {},

    db: {
      latest: [],
      modulos: [],
    },

    ui: {
      valid: false,
      dialog: false,
      loading: false,
      generated: false,
      showErrorEmpty: false,
      modoFirma: 'manual',

      $canvas: null,
      $$wrapper: null,
    },
  }),

  computed: {
    addonFirmaDesdeArchivoActivo() {
      return this.$auth?.user?.empresaOwner?.configuracion?.addonFirmaDesdeArchivo === true
    },

    hayArchivoFirmaSeleccionado() {
      return !!this.archivoFirmaCargado.archivo
    },

    permiteCargarFirmaDesdeArchivo() {
      return (
        this.addonFirmaDesdeArchivoActivo &&
        this.options?.permitirCargaArchivoFirma === true &&
        this.options?.type !== 'croquis'
      )
    },

    textoBotonPrincipal() {
      if (this.hayArchivoFirmaSeleccionado && this.ui.modoFirma === 'archivo') {
        return this.$t('signatureSign.save')
      }

      return this.options.type == 'croquis'
        ? this.$t('signatureSign.save')
        : this.$t('signatureSign.sign')
    },

    textoBotonCargaImagen() {
      return this.hayArchivoFirmaSeleccionado
        ? this.$t('signatureSign.changeSignatureImage')
        : this.$t('signatureSign.uploadSignatureImage')
    },

    esEdicionFirma() {
      return !!this.options?.firmaExistente
    },

    isClient() {
      return this.options?.typeOfUser == 'cliente' || this.$store.getters.isCliente
    },

    latest() {
      return this.db.latest.slice(0, 3)
    },
  },

  methods: {
    open(payload) {
      this.options = { ...payload }
      if (payload.isNew == true) {
        delete this.options.isNew
        delete this.options.modulo
        delete this.options.moduloRef
        delete this.options.submodulo
        delete this.options.submoduloRef
      }

      this.ui.dialog = true
      this.ui.showErrorEmpty = false
      this.ui.modoFirma = 'manual'
      this.reiniciarArchivoFirmaCargado()

      this.startSignaturePad()
    },

    close() {
      this.files = []
      this.options = {}
      this.reiniciarArchivoFirmaCargado()

      this.ui.dialog = false
      this.ui.loading = false
      this.ui.generated = false
      this.ui.modoFirma = 'manual'

      if (this.ui.$canvas) {
        this.ui.$canvas.style.opacity = 0
      }
    },

    validarConfiguracionFirma() {
      if ('modulo_ref' in this.options || 'submodulo_ref' in this.options) {
        throw new Error(
          'Signatures: options debe usar moduloRef/submoduloRef. No se admite modulo_ref/submodulo_ref.'
        )
      }

      if ('can_replace' in this.options) {
        throw new Error(
          'Signatures: options debe usar puedeReemplazarFirma. No se admite can_replace.'
        )
      }

      if (this.esEdicionFirma && !this.options.firmaExistente?.uuid) {
        throw new Error('Signatures: la firma en edicion debe incluir firmaExistente.uuid.')
      }
    },

    activarModoManual() {
      this.ui.modoFirma = 'manual'
      this.reiniciarArchivoFirmaCargado()
      this.ui.showErrorEmpty = false

      this.$nextTick(() => {
        if (this.signaturePad && this.ui.$canvas) {
          this.ui.$canvas.style.opacity = 1
          this.resizeCanvas()
        }
      })
    },

    alCancelar() {
      if (this.hayArchivoFirmaSeleccionado && this.ui.modoFirma === 'archivo') {
        this.cancelarArchivoFirmaSeleccionado()
        return
      }

      this.close()
    },

    startSignaturePad() {
      setTimeout(() => {
        this.ui.$wrapper = document.getElementById('signaturePad-wrapper')
        this.ui.$canvas = document.getElementById('signaturePad')

        this.signaturePad = new SignaturePad(this.ui.$canvas, {
          penColor: 'rgb(0, 0, 0)',
        })

        this.resizeCanvas()
      }, 200)
    },

    cleanSignaturePad() {
      if (this.signaturePad) {
        this.signaturePad.clear()
      }

      this.reiniciarArchivoFirmaCargado()
      this.ui.modoFirma = 'manual'
      this.ui.showErrorEmpty = false

      this.$nextTick(() => {
        if (this.signaturePad && this.ui.$canvas) {
          this.ui.$canvas.style.opacity = 1
          this.resizeCanvas()
        }
      })
    },

    cancelarArchivoFirmaSeleccionado() {
      this.reiniciarArchivoFirmaCargado()
      this.ui.modoFirma = 'manual'
      this.ui.showErrorEmpty = false

      this.$nextTick(() => {
        if (this.signaturePad && this.ui.$canvas) {
          this.ui.$canvas.style.opacity = 1
          this.resizeCanvas()
        }
      })
    },

    resizeCanvas() {
      const ratio = Math.max(window.devicePixelRatio || 1, 1)
      const wrapperWidth = this.ui.$wrapper.offsetWidth - 28
      const wrapperHeight = this.options.type == 'croquis' ? 400 : 250

      this.ui.$canvas.width = wrapperWidth * ratio
      this.ui.$canvas.height = wrapperHeight * ratio
      this.ui.$canvas.getContext('2d').scale(ratio, ratio)

      this.signaturePad.clear()
      this.ui.$canvas.style.opacity = 1
    },

    signatureToFile() {
      let dataURI = this.signaturePad.toDataURL('image/png')

      var byteString = atob(dataURI.split(',')[1])
      var mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]

      var ab = new ArrayBuffer(byteString.length)
      var ia = new Uint8Array(ab)
      for (var i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i)
      }

      return new Blob([ab], { type: mimeString })
    },

    abrirSelectorArchivoFirma() {
      this.ui.modoFirma = 'archivo'
      this.$refs.inputArchivoFirma?.click()
    },

    async alSeleccionarArchivoFirma(event) {
      const [archivo] = event?.target?.files || []

      if (!archivo) {
        this.reiniciarArchivoFirmaCargado()
        this.ui.modoFirma = 'manual'

        this.$nextTick(() => {
          if (this.signaturePad && this.ui.$canvas) {
            this.ui.$canvas.style.opacity = 1
            this.resizeCanvas()
          }
        })
        return
      }

      if (!archivo.type || !archivo.type.startsWith('image/')) {
        this.reiniciarArchivoFirmaCargado()
        this.ui.modoFirma = 'manual'

        this.$store.commit('notification/show', {
          text: this.$t('signatureSign.invalidSignatureFile'),
          color: 'error',
          timeout: 3000,
        })
        return
      }

      try {
        const base64 = await this.convertirArchivoABase64(archivo)
        this.archivoFirmaCargado = {
          archivo,
          base64,
          nombre: archivo.name,
        }
        this.ui.modoFirma = 'archivo'
        this.ui.showErrorEmpty = false
      } catch (e) {
        this.reiniciarArchivoFirmaCargado()
        this.ui.modoFirma = 'manual'

        this.$store.commit('notification/show', {
          text: this.$t('signatureSign.invalidSignatureFile'),
          color: 'error',
          timeout: 3000,
        })
      }
    },

    convertirArchivoABase64(archivo) {
      return new Promise((resolve, reject) => {
        const lector = new FileReader()

        lector.onload = () => resolve(lector.result)
        lector.onerror = reject

        lector.readAsDataURL(archivo)
      })
    },

    reiniciarArchivoFirmaCargado() {
      this.archivoFirmaCargado = {
        archivo: null,
        base64: null,
        nombre: '',
      }

      if (this.$refs.inputArchivoFirma) {
        this.$refs.inputArchivoFirma.value = null
      }
    },

    async submit() {
      let jxr = false
      let step = false

      if (this.validate()) step = 'valid'

      if (step === 'valid') {
        jxr = await this.store()
      }

      if (step === 'valid' && jxr !== false) {
        this.$nuxt.$emit('signatures:signed', {
          data: jxr,
          payload: this.options.payload,
          opcionesFirma: this.options,
        })

        this.close()
      }
    },

    validate() {
      let isValid = !!this.archivoFirmaCargado.archivo || !this.signaturePad.isEmpty()
      if (!isValid) this.ui.showErrorEmpty = true

      return isValid
    },

    async store() {
      let body = { ...this.options }
      this.validarConfiguracionFirma()

      body.file = this.archivoFirmaCargado.archivo || this.signatureToFile()
      body.base64 = this.archivoFirmaCargado.base64 || this.signaturePad.toDataURL('image/png')
      body.identificadorFirma = this.options.firmaExistente?.uuid

      if (!body.created_by) body.created_by = this.user.uuid

      try {
        const accionFirma = this.esEdicionFirma ? 'signatures/update' : 'signatures/create'
        const xhr = await this.$store.dispatch(accionFirma, body)
        return { ...body, ...xhr }
      } catch (e) {
        const textoError =
          e?.response?.data?.['hydra:description'] ||
          e?.response?.data?.message ||
          e?.message ||
          'Ha ocurrido un error generando la firma'

        console.error('Signatures: error al guardar la firma.', e)
        this.$store.commit('notification/show', {
          text: textoError,
          color: 'error',
          timeout: 5000,
        })

        return false
      }
    },

    loadModulos() {
      this.db.modulos = MODULOS
    },

    async getLatestSignatures() {
      let xhr = await this.$store.dispatch('signatures/latest', this.user.uuid)
      if (xhr) {
        this.db.latest = xhr
      }
    },

    async init() {
      this.user = this.$auth.user

      this.loadModulos()
      this.getLatestSignatures()

      this.$nuxt.$on('signatures:open', (payload) => {
        this.open(payload)
      })
    },
  },

  mounted() {
    this.init()
  },

  beforeDestroy() {
    this.$nuxt.$off('signatures:open')
  },
}
</script>

<style>
.v-dialog.v-dialog--active::-webkit-scrollbar {
  width: 8px;
}

.v-dialog.v-dialog--active::-webkit-scrollbar-track {
  background: transparent;
}

.v-dialog.v-dialog--active::-webkit-scrollbar-thumb {
  background-color: #00000069;
  border-radius: 15px;
  border: 1px solid #a5a5a5;
}

.signatureCard {
  border-radius: 18px !important;
  overflow: hidden;
  box-shadow: 0 14px 35px rgba(25, 35, 52, 0.12) !important;
  background: #ffffff;
}

.signatureCard__header {
  padding: 24px 28px 10px;
  background: linear-gradient(180deg, #fafbff 0%, #ffffff 100%);
  border-bottom: 1px solid #edf0f5;
}

.signatureCard__eyebrow {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 6px;
}

.signatureCard__title {
  margin: 0;
  font-size: 24px;
  line-height: 1.2;
  font-weight: 700;
  color: #1f2937;
}

.signatureCard__subtitle {
  margin: 8px 0 0;
  font-size: 14px;
  color: #6b7280;
}

.signatureCard__content {
  padding: 22px 28px 18px !important;
}

.signatureModeSelector {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.signatureModeCard {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #fafafa;
  transition: all 0.2s ease;
}

.signatureModeCard--active {
  border-color: #7c4dff;
  background: #f6f1ff;
  box-shadow: 0 0 0 3px rgba(124, 77, 255, 0.08);
}

.signatureModeCard__icon {
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border: 1px solid #ececec;
}

.signatureModeCard__body {
  flex: 1;
  min-width: 0;
}

.signatureModeCard__title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
}

.signatureModeCard__text {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
  line-height: 1.35;
}

.signatureModeCard__action {
  display: flex;
  align-items: center;
}

.signaturePanel {
  border: 1px solid #e8ebf2;
  border-radius: 16px;
  background: #fcfcfd;
  padding: 14px;
}

.signaturePanel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.signaturePanel__title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
}

.signaturePanel__hint {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
}

.signaturePanel__canvasWrapper {
  width: 100%;
}

.signaturePad {
  display: block;
  width: 100%;
  border-radius: 12px;
  background: #f4f6f8;
  border: 2px dashed #cfd6df !important;
  transition: all 0.25s ease-in-out;
}

.signaturePad:hover {
  border-color: #b6c1ce !important;
  background: #f8fafc;
}

.signatureUploadPreview {
  border: 1px solid #e8ebf2;
  border-radius: 16px;
  background: #fcfcfd;
  padding: 16px;
}

.signatureUploadPreview__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.signatureUploadPreview__title {
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 4px;
}

.signatureUploadPreview__name {
  font-size: 13px;
  color: #6b7280;
  word-break: break-word;
}

.signatureUploadPreview__removeBtn {
  margin-top: -4px;
}

.signatureUploadPreview__imageWrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  border-radius: 12px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #eceff4;
  padding: 12px;
}

.signaturePreview {
  display: block;
  max-width: 100%;
  max-height: 220px;
  margin: 0 auto;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #d9dee7;
  object-fit: contain;
}

.signatureMainButton {
  height: 46px !important;
  border-radius: 12px !important;
  font-size: 14px !important;
  font-weight: 700;
  letter-spacing: 0.01em;
  text-transform: none !important;
  box-shadow: 0 8px 18px rgba(46, 160, 67, 0.18);
}

.signatureError {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff4f4;
  color: #c62828;
  font-size: 13px;
  font-weight: 500;
}

.signatureCard__actions {
  padding: 12px 20px 16px !important;
}

.signatureActionBtn {
  text-transform: none !important;
  font-weight: 600;
}

.signatureCard__loading {
  padding: 16px 24px 18px !important;
}

@media (max-width: 760px) {
  .signatureCard__header {
    padding: 20px 18px 8px;
  }

  .signatureCard__content {
    padding: 18px !important;
  }

  .signatureModeSelector {
    grid-template-columns: 1fr;
  }

  .signatureModeCard {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .signatureModeCard__action {
    width: 100%;
  }

  .signatureModeCard__action .v-btn {
    width: 100%;
  }

  .signaturePanel__header,
  .signatureUploadPreview__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .signaturePanel__hint {
    white-space: normal;
  }
}
</style>